import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, LogIn, RefreshCw, WifiOff, XCircle } from 'lucide-react';
import { WebsiteHeader } from '../Dashboard/WebsiteHeader';
export type PaymentFailureKind = 'cancelled' | 'failed' | 'expired' | 'unconfirmed' | 'timeout' | 'session' | 'missing_reference';
interface PaymentInterruptedStateProps {
  kind: PaymentFailureKind;
  message: string | null;
  /** The report the user was unlocking, if known. */
  scanId: string | null;
  onRetry?: () => void;
}
const COPY: Record<PaymentFailureKind, {
  title: string;
  body: string;
  icon: typeof XCircle;
  reportParam: string;
}> = {
  cancelled: {
    title: 'Payment cancelled',
    body: 'Checkout was closed before payment completed. You were not charged.',
    icon: XCircle,
    reportParam: '&payment=cancelled'
  },
  failed: {
    title: 'Payment didn’t go through',
    body: 'Your payment couldn’t be completed and your report is still locked. If you were charged, contact support with your receipt and we’ll resolve it.',
    icon: AlertTriangle,
    reportParam: '&payment=failed'
  },
  expired: {
    title: 'Checkout session expired',
    body: 'Your checkout session timed out before payment. You were not charged — start a new checkout from your report.',
    icon: Clock,
    reportParam: '&payment=expired'
  },
  unconfirmed: {
    title: 'We couldn’t confirm your payment',
    body: 'The connection dropped while confirming. If you completed payment, your report will unlock automatically — you won’t be charged twice.',
    icon: WifiOff,
    reportParam: ''
  },
  timeout: {
    title: 'Still confirming your payment',
    body: 'Confirmation is taking longer than usual. If you were charged, your report will unlock automatically within a few minutes.',
    icon: Clock,
    reportParam: ''
  },
  session: {
    title: 'Sign in to finish unlocking',
    body: 'Your session expired while you were at checkout. Sign in again and your report will be in your dashboard. Any completed payment is kept.',
    icon: LogIn,
    reportParam: ''
  },
  missing_reference: {
    title: 'Payment not found',
    body: 'We couldn’t find a payment for this checkout. You were not charged.',
    icon: AlertTriangle,
    reportParam: ''
  }
};

/** Every interrupted checkout lands here — and always offers a way back to the report. */
export function PaymentInterruptedState({
  kind,
  message,
  scanId,
  onRetry
}: PaymentInterruptedStateProps) {
  const copy = COPY[kind];
  const Icon = copy.icon;
  const reportHref = scanId ? `/analysis?id=${scanId}${copy.reportParam}` : '/dashboard';
  const showRetry = Boolean(onRetry) && (kind === 'unconfirmed' || kind === 'timeout');
  return <div className="flex min-h-screen w-full flex-col bg-[#050505] text-white" data-id="element-1587" data-mp="2rpgpznxrhcd1">
      <WebsiteHeader data-id="element-1588" data-mp="uukkwqccv79w" />
      <main className="mx-auto w-full max-w-md flex-1 px-5 pb-10 pt-28" data-id="element-1589" data-mp="uuucgid1tmhp">
        <div className="border border-white/10 bg-[#0a0a0a] p-8 text-center" data-id="element-1590" data-mp="pxkeuffia4is">
          <Icon className="mx-auto text-[#FF5A5A]" size={32} aria-hidden="true" data-id="element-1591" data-mp="xglayfnz8vw4" />
          <h1 className="mt-4 text-2xl font-black uppercase tracking-tight" data-id="element-1592" data-mp="12f4u68b6rtks">
            {copy.title}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-1593" data-mp="175blwh0zvu79">{copy.body}</p>
          {kind === 'failed' && message && <p className="mt-3 font-mono text-xs text-gray-500" data-id="element-1594" data-mp="1c3mw4sv7c6q0">{message}</p>}
          <div className="mt-6 flex flex-col gap-3" data-id="element-1595" data-mp="ei8zgn9lf9xd">
            {kind === 'session' ? <Link to="/login" className="inline-flex min-h-[52px] items-center justify-center gap-2 bg-[#E10600] px-6 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-150 ease-out hover:bg-[#FF1A12]" data-id="element-1596" data-mp="jhbtzdry5xr3">
                <LogIn size={16} aria-hidden="true" data-id="element-1597" data-mp="1zv216ted524y" />
                Sign in
              </Link> : <Link to={reportHref} className="inline-flex min-h-[52px] items-center justify-center bg-[#E10600] px-6 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-150 ease-out hover:bg-[#FF1A12]" data-id="element-1598" data-mp="24skawm07fle0">
                {scanId ? 'Return to your report' : 'Go to Dashboard'}
              </Link>}
            {showRetry && <button type="button" onClick={onRetry} className="inline-flex min-h-[48px] items-center justify-center gap-2 border border-white/15 px-6 text-sm font-bold uppercase tracking-wider text-gray-200 transition-colors duration-150 ease-out hover:border-white/30 hover:text-white" data-id="element-1599" data-mp="27d9zii5useca">
                <RefreshCw size={15} aria-hidden="true" data-id="element-1600" data-mp="2q4t06icengb5" />
                Check again
              </button>}
            <a href="mailto:support@toxicdetector.com" className="inline-flex min-h-[44px] items-center justify-center text-sm text-gray-400 transition-colors duration-150 ease-out hover:text-white" data-id="element-1601" data-mp="3510jzhjwifpw">
              Contact support
            </a>
          </div>
        </div>
      </main>
    </div>;
}